// Press kit — shared data source. Edit these values to update the page.

export const COMPANY = {
  name: "AtlasForge Financial",
  founded: "2024",
  hq: "South Carolina, United States",
  founder: "Dr. David Homer",
  website: "https://atlasforgefinancial.com",
  industry: "Consumer FinTech · Personal Finance Infrastructure",
  employees: "1–10",
  media_contact: {
    name: "AtlasForge Media Relations",
    email: "customersupport@atlasforgefinancial.com",
    website: "https://atlasforgefinancial.com/contact",
  },
  socials: {
    linkedin: "https://www.linkedin.com/company/atlasforgefinancial",
    instagram: "https://www.instagram.com/atlasforgefin/",
    facebook: "https://www.facebook.com/atlasforgefinancial",
  },
};

export const SAFE_TO_SPEND = {
  name: "Safe to Spend 365",
  tagline: "Stop guessing. Start spending with confidence.",
  pricing: "Free to download · Premium from $4.99/month",
  platforms: ["iOS (App Store)", "Android (Google Play)"],
  play_store: "https://play.google.com/store/apps/details?id=com.atlasforge.safetospend",
  app_store: "https://apps.apple.com/us/app/safe-to-spend-365/id6792137232",
  one_liner:
    "Safe to Spend 365 is a personal finance app that shows the money you can actually spend today after bills, savings, and goals — without categories, guilt, or manual tracking.",
};

export const EMBER360 = {
  name: "Ember360",
  tagline: "Your money, your portfolio, all in one place.",
  pricing: "Free to download · Premium tiers coming soon",
  platforms: ["iOS (App Store)", "Android (Google Play)"],
  play_store: "https://play.google.com/store/apps/details?id=com.atlasforge.ember360",
  app_store: "https://apps.apple.com/us/app/ember360/id6796043696",
  one_liner:
    "Ember360 is an AI-powered money app that unifies banking, portfolios, subscriptions, and cash-flow forecasting into a single, seamless view.",
};

// Brand
export const BRAND_COLORS = [
  { name: "Deep Navy", hex: "#0A1128", role: "Primary" },
  { name: "Forge Green", hex: "#1A4232", role: "Secondary" },
  { name: "Emerald Signal", hex: "#00C48C", role: "Accent (Safe to Spend 365)" },
  { name: "Ember Coral", hex: "#FF5A36", role: "Accent (Ember360)" },
  { name: "Cloud White", hex: "#FFFFFF", role: "Background" },
  { name: "Slate 500", hex: "#64748B", role: "Secondary text" },
];

// Logo & icon downloads
export const BRAND_ASSETS = {
  atlas_logo_color:
    "/brand/atlasforge-logo.png",
  atlas_logo_full:
    "/brand/atlasforge-logo.png",
  sts_logo:
    "/brand/safe-to-spend-365-logo.png",
  ember_logo:
    "/brand/ember360-logo.png",
};

// Hero / marketing artwork — 3-phone Safe to Spend 365 featured graphic
export const HERO_BANNER =
  "/brand/featured-graphic.jpg";

// App screenshots — press-quality (updated Jul 2026 with Safe to Spend 365 branding)
export const APP_SCREENSHOTS = [
  {
    src: "/brand/screenshots/screenshot-01-home.png",
    alt: "Safe to Spend 365 Home — Stop guessing, start spending with confidence. See your real safe to spend amount every day.",
    caption: "Home",
  },
  {
    src: "/brand/screenshots/screenshot-02-accounts.png",
    alt: "Safe to Spend 365 Accounts — Your money. Your plan. Spend every day with confidence. Net worth, cash position, and projected payday balance.",
    caption: "Accounts",
  },
  {
    src: "/brand/screenshots/screenshot-03-goals.png",
    alt: "Safe to Spend 365 Goals & Plans — Your future selves are counting on you. Journey levels from Start to Wealth with safety buffer.",
    caption: "Goals",
  },
  {
    src: "/brand/screenshots/screenshot-04-plan-today.png",
    alt: "Safe to Spend 365 Plan today — Build your future. Savings goals, safety buffer, and debt payoff planning.",
    caption: "Plan Today",
  },
  {
    src: "/brand/screenshots/screenshot-05-insights.png",
    alt: "Safe to Spend 365 Insights — Understand today, plan for tomorrow. Financial health 72/100, Runway, and Safe-to-Spend consistency.",
    caption: "Insights",
  },
  {
    src: "/brand/screenshots/screenshot-06-cashflow.png",
    alt: "Safe to Spend 365 Cash flow forecast — See your future, spend with confidence. On-track projection with buffer line.",
    caption: "Cash Flow Forecast",
  },
  {
    src: "/brand/screenshots/screenshot-07-insights-trends.png",
    alt: "Safe to Spend 365 Insights & Trends — Turn insights into action. Runway, Safe-to-Spend consistency, and trends over time.",
    caption: "Insights & Trends",
  },
  {
    src: "/brand/screenshots/screenshot-08-autopilot.png",
    alt: "Safe to Spend 365 Autopilot — Your money, smarter answers. Ask anything about your money — powered by Claude Sonnet 4.5.",
    caption: "AI Autopilot",
  },
];

// Founder photos
export const FOUNDER_PHOTOS = [
  {
    src: "/brand/people/founder-headshot.png",
    alt: "AtlasForge Financial founder headshot",
    caption: "Headshot (color)",
  },
];

// Key features
export const KEY_FEATURES = [
  { title: "Safe to Spend 365 Today™", body: "One number that tells you exactly how much you can spend today after bills, savings, and goals." },
  { title: "Bank Connection", body: "Securely link every checking, savings, and credit card via Plaid — updated in real time." },
  { title: "AI Financial Coach (Autopilot)", body: "Powered by Claude Sonnet 4.5 — ask any question about your money and get context-aware answers." },
  { title: "Can I Afford It?", body: "Instant yes/no decision-support for one-off purchases, factoring in upcoming commitments." },
  { title: "Spending Insights", body: "Automatic subscription detection, category trends, and financial health scoring." },
  { title: "Daily Spending Guidance", body: "Every morning, Today's Coach highlights the single most important money action for the day." },
  { title: "Savings Goals", body: "Set targets, watch progress, and automatically protect committed dollars from casual spending." },
  { title: "Protected Buffer", body: "A fixed cushion — automatically subtracted from your Safe to Spend 365 number, keeping you out of overdraft." },
];

// FAQ
export const FAQ = [
  {
    q: "Is Safe to Spend 365 free?",
    a: "Safe to Spend 365 is free to download and free to use for its core Safe-to-Spend Today calculation, bank connection, and daily coaching. Premium features — including the full AI Autopilot chat experience, advanced cash-flow forecasting, and unlimited savings goals — are available for $4.99 per month.",
  },
  {
    q: "Which banks work with Safe to Spend 365?",
    a: "Any US bank supported by Plaid — over 12,000 institutions including all major national banks, credit unions, and neobanks.",
  },
  {
    q: "Is Plaid secure?",
    a: "Yes. Plaid uses bank-grade encryption, never stores your credentials, and is used by companies like Venmo, Robinhood, and SoFi. We access only read-only transaction data — we cannot move or withdraw funds.",
  },
  {
    q: "Does the AI Coach access my money?",
    a: "No. The AI Coach has read-only access to your transaction data via Plaid. It cannot transfer, move, or spend a single dollar. It's an assistant, not an account holder.",
  },
  {
    q: "Does Safe to Spend 365 move my money?",
    a: "No. Safe to Spend 365 is a read-only insights layer. Actual transfers, payments, and card activity happen inside your bank's own app or website.",
  },
  {
    q: "Is my data encrypted?",
    a: "Yes. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We are SOC 2 aligned and GDPR compliant.",
  },
  {
    q: "How does the Safe to Spend 365 calculation work?",
    a: "Available balance minus (bills due before next paycheck) minus (committed savings + goal transfers) minus (protected buffer). The number updates in real time as your accounts move.",
  },
  {
    q: "Where is AtlasForge Financial based?",
    a: "AtlasForge Financial is headquartered in South Carolina, United States.",
  },
];

// Testimonials — replace with real user quotes when available.
export const TESTIMONIALS = [
  {
    quote: "Finally a budgeting app that actually tells me what I can safely spend.",
    author: "Beta user, verified via Google Play review",
  },
  {
    quote:
      "I tried YNAB, Mint, and Copilot before this. Safe to Spend 365 is the only one I still open every day.",
    author: "Early adopter, iOS TestFlight",
  },
  {
    quote:
      "The AI Coach caught two subscriptions I forgot about and told me exactly which credit card to pay down first.",
    author: "Beta user, verified email",
  },
];

// Press release (launch template — edit dates when real)
export const PRESS_RELEASE = {
  headline:
    "AtlasForge Financial Launches Safe to Spend 365 — the Personal Finance App That Tells You Exactly What You Can Spend Today",
  city: "Charleston, SC",
  date: "February 2026",
  body: `AtlasForge Financial today announced the public launch of Safe to Spend 365, a personal finance app for iOS and Android that eliminates traditional budgeting in favor of one honest daily answer: how much you can actually spend today without missing bills, savings, or financial goals. Safe to Spend 365 is free to download and free to use for its core daily calculation, with an optional Premium tier at $4.99 per month that unlocks the full AI Autopilot experience, advanced cash-flow forecasting, and unlimited savings goals.

Unlike category-based budgeting apps that require constant manual tracking, Safe to Spend 365 automatically computes a real-time "safe to spend" number by combining live bank balances with upcoming commitments, protected savings buffers, and user-set goals. Users see a single number, updated continuously, that answers the one question every purchase actually raises: "Can I spend this?"

"I've spent my career as a budget analyst — running cash-flow models for organizations that move millions of dollars a month," said Dr. David Homer, founder of AtlasForge Financial. "Then I'd come home and watch close friends and family overdraft their accounts three days before payday, with no idea where their money had gone. The same math that keeps an organization solvent can keep a household solvent — we just needed to build it into an app. That's Safe to Spend 365. It isn't a discipline problem people are facing; it's a design problem, and we finally fixed it."

Safe to Spend 365 integrates with over 12,000 US financial institutions via Plaid, features an AI-powered assistant ("Autopilot") built on Anthropic's Claude Sonnet 4.5, and includes protected buffers, subscription detection, financial health scoring, and cash-flow forecasting. The app is available today at no cost on the Google Play Store and iOS App Store.

AtlasForge Financial also operates a partner-facing API platform that powers the Safe to Spend 365 calculation for banks, credit unions, and fintechs. Financial institutions interested in embedding Safe to Spend 365 can visit https://atlasforgefinancial.com/platform.

About AtlasForge Financial
AtlasForge Financial develops consumer financial technology that helps people make confident spending decisions through personalized budgeting, banking integrations, and AI-powered financial guidance. The company is headquartered in South Carolina.

Media Contact
customersupport@atlasforgefinancial.com
https://atlasforgefinancial.com/contact`,
};
